<template>
  <div class="sensor-extractions-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Sensor Extractions</h1>
          <p>Gestión de extracciones de datos de sensores</p>
        </v-col>
      </v-row>
      
      <!-- Estadísticas rápidas -->
      <v-row>
        <v-col cols="12" md="3">
          <v-card color="primary" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-sensors</v-icon>
                <div>
                  <div class="text-h4">{{ totalExtractions }}</div>
                  <div class="text-caption">Total Extracciones</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="success" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-check-circle</v-icon>
                <div>
                  <div class="text-h4">{{ activeCount }}</div>
                  <div class="text-caption">Activas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="warning" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-clock-alert</v-icon>
                <div>
                  <div class="text-h4">{{ overdueCount }}</div>
                  <div class="text-caption">Atrasadas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="3">
          <v-card color="info" variant="tonal">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon size="40" class="mr-3">mdi-sync</v-icon>
                <div>
                  <div class="text-h4">{{ recentlySyncedCount }}</div>
                  <div class="text-caption">Recién Sincronizadas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Filtros -->
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedSensorSettingId"
            :items="sensorSettingOptions"
            label="Filtrar por Configuración"
            clearable
            @update:model-value="filterBySensorSetting"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedType"
            :items="typeOptions"
            label="Filtrar por Tipo"
            clearable
            @update:model-value="filterByType"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filtrar por Estado"
            clearable
            @update:model-value="filterByStatus"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn 
            color="secondary" 
            block 
            @click="bulkSync"
            :disabled="selectedItems.length === 0"
            :loading="bulkSyncLoading"
          >
            <v-icon left>mdi-sync</v-icon>
            Sincronizar Seleccionadas ({{ selectedItems.length }})
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            v-model="selectedItems"
            :headers="headers"
            :items="sensorExtractionsList"
            :loading="loading"
            item-value="id"
            show-select
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Extracciones de Sensores</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Extracción
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.sensorSettingId="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.sensorSettingId }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip :color="getTypeColor(item.type)" small>
                <v-icon left size="small">{{ getTypeIcon(item.type) }}</v-icon>
                {{ item.type.toUpperCase() }}
              </v-chip>
            </template>
            <template v-slot:item.pollingInterval="{ item }">
              <v-chip color="indigo" variant="outlined" small>
                {{ formatInterval(item.pollingInterval) }}
              </v-chip>
            </template>
            <template v-slot:item.lastSyncAt="{ item }">
              <div class="d-flex flex-column">
                <span class="text-caption">{{ formatDateTime(item.lastSyncAt) }}</span>
                <v-chip 
                  :color="item.isOverdue() ? 'error' : 'success'" 
                  size="x-small"
                >
                  {{ item.getTimeSinceLastSync() }}
                </v-chip>
              </div>
            </template>
            <template v-slot:item.nextSync="{ item }">
              <div class="d-flex flex-column">
                <span class="text-caption" :class="{ 'text-error': item.isOverdue() }">
                  {{ formatDateTime(item.getNextSyncTime()) }}
                </span>
                <v-chip 
                  :color="item.isOverdue() ? 'error' : 'info'" 
                  size="x-small"
                >
                  {{ item.getTimeUntilNextSync() }}
                </v-chip>
              </div>
            </template>
            <template v-slot:item.active="{ item }">
              <v-switch
                v-model="item.active"
                :loading="toggleLoading[item.id]"
                color="success"
                @change="toggleStatus(item)"
                hide-details
                density="compact"
              ></v-switch>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item)" small>
                <v-icon left size="small">{{ getStatusIcon(item) }}</v-icon>
                {{ getStatusText(item) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn 
                size="small" 
                icon 
                variant="text" 
                @click="syncNow(item)"
                :loading="syncLoading[item.id]"
                :disabled="!item.active"
              >
                <v-icon>mdi-sync</v-icon>
                <v-tooltip activator="parent" location="top">
                  Sincronizar ahora
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">
                  Editar extracción
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">
                  Eliminar extracción
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from 'vue'
import { useSensorExtractionStore } from './store'
import { SensorExtraction } from './models/sensorExtraction'

const sensorExtractionStore = useSensorExtractionStore()
const loading = ref(false)
const bulkSyncLoading = ref(false)
const selectedSensorSettingId = ref<string | null>(null)
const selectedType = ref<string | null>(null)
const statusFilter = ref<string | null>(null)
const selectedItems = ref<string[]>([])
const syncLoading = reactive<Record<string, boolean>>({})
const toggleLoading = reactive<Record<string, boolean>>({})

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Config ID', key: 'sensorSettingId' },
  { title: 'Tipo', key: 'type' },
  { title: 'Setting ID', key: 'settingId' },
  { title: 'Intervalo', key: 'pollingInterval' },
  { title: 'Última Sync', key: 'lastSyncAt' },
  { title: 'Próxima Sync', key: 'nextSync' },
  { title: 'Activo', key: 'active' },
  { title: 'Estado', key: 'status' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros
const sensorSettingOptions = ref([
  { title: 'Sensor Config 1', value: 'sensorConfig1' },
  { title: 'Sensor Config 2', value: 'sensorConfig2' },
  { title: 'Sensor Config 3', value: 'sensorConfig3' },
])

const typeOptions = ref([
  { title: 'FTP', value: 'ftp' },
  { title: 'API', value: 'api' },
  { title: 'Database', value: 'database' },
  { title: 'MQTT', value: 'mqtt' },
  { title: 'Socket', value: 'socket' },
])

const statusOptions = ref([
  { title: 'Activo', value: 'active' },
  { title: 'Inactivo', value: 'inactive' },
  { title: 'Atrasado', value: 'overdue' },
  { title: 'Sincronizado', value: 'synced' },
])

const sensorExtractionsList = computed(() => {
  let list = sensorExtractionStore.sensorExtractions
  
  if (selectedSensorSettingId.value) {
    list = list.filter(item => item.sensorSettingId === selectedSensorSettingId.value)
  }
  
  if (selectedType.value) {
    list = list.filter(item => item.type === selectedType.value)
  }
  
  if (statusFilter.value) {
    switch (statusFilter.value) {
      case 'active':
        list = list.filter(item => item.active)
        break
      case 'inactive':
        list = list.filter(item => !item.active)
        break
      case 'overdue':
        list = list.filter(item => item.isOverdue())
        break
      case 'synced':
        list = list.filter(item => !item.isOverdue() && item.active)
        break
    }
  }
  
  return list
})

// Estadísticas computadas
const totalExtractions = computed(() => sensorExtractionStore.sensorExtractions.length)
const activeCount = computed(() => sensorExtractionStore.sensorExtractions.filter(item => item.active).length)
const overdueCount = computed(() => sensorExtractionStore.sensorExtractions.filter(item => item.isOverdue()).length)
const recentlySyncedCount = computed(() => {
  const thirtyMinutesAgo = new Date()
  thirtyMinutesAgo.setMinutes(thirtyMinutesAgo.getMinutes() - 30)
  return sensorExtractionStore.sensorExtractions.filter(item => 
    new Date(item.lastSyncAt) >= thirtyMinutesAgo
  ).length
})

const formatInterval = (minutes: number) => {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

const formatDateTime = (date: Date | string) => {
  return new Date(date).toLocaleString('es-ES', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    ftp: 'blue',
    api: 'green',
    database: 'purple',
    mqtt: 'orange',
    socket: 'red'
  }
  return colors[type] || 'grey'
}

const getTypeIcon = (type: string) => {
  const icons: Record<string, string> = {
    ftp: 'mdi-folder-network',
    api: 'mdi-api',
    database: 'mdi-database',
    mqtt: 'mdi-message-processing',
    socket: 'mdi-power-socket'
  }
  return icons[type] || 'mdi-help-circle'
}

const getStatusColor = (item: SensorExtraction) => {
  if (!item.active) return 'grey'
  if (item.isOverdue()) return 'error'
  return 'success'
}

const getStatusIcon = (item: SensorExtraction) => {
  if (!item.active) return 'mdi-pause'
  if (item.isOverdue()) return 'mdi-clock-alert'
  return 'mdi-check-circle'
}

const getStatusText = (item: SensorExtraction) => {
  if (!item.active) return 'Inactivo'
  if (item.isOverdue()) return 'Atrasado'
  return 'Al día'
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorExtractionStore.getSensorExtractions()
  } finally {
    loading.value = false
  }
}

const filterBySensorSetting = async (sensorSettingId: string | null) => {
  if (sensorSettingId) {
    loading.value = true
    try {
      await sensorExtractionStore.getSensorExtractionsBySensorSettingId(sensorSettingId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByType = async (type: string | null) => {
  if (type) {
    loading.value = true
    try {
      await sensorExtractionStore.getSensorExtractionsByType(type)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByStatus = () => {
  // El filtrado por estado se maneja en el computed
}

const addItem = () => {
  console.log('Add new sensor extraction')
}

const editItem = (item: SensorExtraction) => {
  console.log('Edit item:', item)
}

const deleteItem = (item: SensorExtraction) => {
  console.log('Delete item:', item)
}

const syncNow = async (item: SensorExtraction) => {
  syncLoading[item.id] = true
  try {
    await sensorExtractionStore.syncSensorExtraction(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error syncing extraction:', error)
  } finally {
    syncLoading[item.id] = false
  }
}

const toggleStatus = async (item: SensorExtraction) => {
  toggleLoading[item.id] = true
  try {
    await sensorExtractionStore.toggleSensorExtractionStatus(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error toggling status:', error)
  } finally {
    toggleLoading[item.id] = false
  }
}

const bulkSync = async () => {
  if (selectedItems.value.length === 0) return
  
  bulkSyncLoading.value = true
  try {
    await sensorExtractionStore.bulkSyncSensorExtractions(selectedItems.value)
    selectedItems.value = []
    await refreshData()
  } catch (error) {
    console.error('Error bulk syncing extractions:', error)
  } finally {
    bulkSyncLoading.value = false
  }
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.sensor-extractions-view {
  height: 100%;
}

.text-error {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
